import { Link } from '@tanstack/react-router';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  HelpCircle,
  Info,
  Loader,
  RotateCcw,
  Wrench,
  XCircle,
} from 'lucide-react';
import { createContext, Fragment, useContext, useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Card,
  ConfirmDialog,
  ErrorState,
  SkeletonRows,
  Table,
  TableWrap,
  Td,
  Th,
} from '../../../components/ui';
import type { MessageKey } from '../../../i18n';
import { useFormat, useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import {
  useAutomationCan,
  useReadiness,
  useRepairAccount,
  useTestPlan,
  useUnspendAccount,
} from '../hooks';
import type {
  CapabilityDef,
  CapabilityResult,
  ReadinessAccount,
  ReadinessClass,
} from '../testrun-types';
import { Segmented, useAutomationErrorText, useFailToast } from './shared';

/**
 * «Ready for tests»: which class of a project goes on which pool account
 * (TestRun src/pages/ReadinessTab.tsx). A class's requirements are
 * cases.<Class>.requires in the repository's testdata (the scanner picks them
 * up); an account's capabilities are its last check.
 *
 * The pool has dozens of accounts, so not a column per account. By classes —
 * a row per set of requirements: how many accounts fit and why the rest do
 * not, the list on a click. By accounts — only the capabilities the project needs.
 *
 * What tests «ate» (a card left blocked, no main account) is repaired right
 * here. An account given to a «new account on every run» class no longer fits
 * that class — «Back in use» removes the mark.
 */

type Verdict = {
  kind: 'ok' | 'missing' | 'unknown';
  text: string;
  keys?: string[];
  spent?: boolean;
};

/** What the account rows may do: repair a capability, put a used-up account back in use. */
interface Actions {
  repairs: Record<string, string>;
  canAct: boolean;
  repair: (account: ReadinessAccount, key: string) => void;
  unspend: (account: ReadinessAccount, className: string) => void;
}
const ActionsContext = createContext<Actions>({
  repairs: {},
  canAct: false,
  repair: () => undefined,
  unspend: () => undefined,
});

const REPAIR_WARN: Record<string, MessageKey> = {
  openCard: 'automation.project.readiness.repairWarn.openCard',
  mainAccount: 'automation.project.readiness.repairWarn.mainAccount',
  twoAccounts: 'automation.project.readiness.repairWarn.twoAccounts',
  loanPayFunds: 'automation.project.readiness.repairWarn.loanPayFunds',
  loanPartialFunds: 'automation.project.readiness.repairWarn.loanPartialFunds',
};

const accountTitle = (account: ReadinessAccount) =>
  account.companyName || account.label || account.type;
const shortOf = (caps: Map<string, CapabilityDef>, key: string) => caps.get(key)?.short ?? key;

/* ─── shared cells ─── */

/** The last repair: running — with a spinner; its outcome — for a day, then it stops cluttering. */
const RepairStatus = ({ account }: { account: ReadinessAccount }) => {
  const { t } = useT();
  const repair = account.repair;
  if (!repair) return null;
  if (repair.status === 'running') {
    return (
      <span className="flex items-center gap-1 text-[12px] text-accent-fg">
        <Loader className="size-3 animate-spin" aria-hidden="true" />
        {t('automation.project.readiness.repairing', { title: repair.title })}
      </span>
    );
  }
  if (!repair.finishedAt || Date.now() - Date.parse(repair.finishedAt) > 24 * 3600_000) return null;
  return (
    <span
      className={cn(
        'block truncate text-[12px]',
        repair.status === 'ok' ? 'text-pass' : 'text-fail',
      )}
      title={repair.message}
    >
      {repair.status === 'ok' ? '✓' : '✗'} {repair.title}: {repair.message}
    </span>
  );
};

/**
 * An account in a line or two: the company is how people know it; the pool's
 * label is a note («loan, point (Colvir …)»), not a name.
 */
const AccountName = ({ account }: { account: ReadinessAccount }) => {
  const { t } = useT();
  const main =
    account.companyName ||
    account.label ||
    t('automation.project.readiness.noLabel', { type: account.type });
  const note = account.companyName ? account.label : null;
  return (
    <div
      className="min-w-0"
      title={[account.companyName, account.label].filter(Boolean).join('\n')}
    >
      <span className="block truncate text-[13px]">{main}</span>
      {note ? <span className="block truncate text-[12px] text-ink-subtle">{note}</span> : null}
      <RepairStatus account={account} />
    </div>
  );
};

/** Under a reason: «Repair» for what can be put right, «Back in use» for a used-up account. */
const FixButtons = ({
  account,
  verdict,
  className,
}: {
  account: ReadinessAccount;
  verdict: Verdict;
  className: string;
}) => {
  const { t } = useT();
  const { repairs, canAct, repair, unspend } = useContext(ActionsContext);
  if (!canAct || account.repair?.status === 'running') return null;
  const fixable = (verdict.keys ?? []).filter((key) => repairs[key]);
  if (!verdict.spent && !fixable.length) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {verdict.spent ? (
        <Button
          size="sm"
          className="h-6 px-2 text-[11.5px]"
          title={t('automation.project.readiness.unspendHint')}
          onClick={() => unspend(account, className)}
        >
          <RotateCcw className="size-3" />
          {t('automation.project.readiness.unspend')}
        </Button>
      ) : null}
      {fixable.map((key) => (
        <Button
          key={key}
          size="sm"
          className="h-6 px-2 text-[11.5px]"
          title={REPAIR_WARN[key] ? t(REPAIR_WARN[key]) : undefined}
          onClick={() => repair(account, key)}
        >
          <Wrench className="size-3" />
          {repairs[key]}
        </Button>
      ))}
    </span>
  );
};

const VerdictIcon = ({ kind }: { kind: Verdict['kind'] }) =>
  kind === 'ok' ? (
    <CheckCircle2 className="size-3.5 shrink-0 text-pass" aria-hidden="true" />
  ) : kind === 'missing' ? (
    <XCircle className="size-3.5 shrink-0 text-fail" aria-hidden="true" />
  ) : (
    <CircleDashed className="size-3.5 shrink-0 text-ink-subtle" aria-hidden="true" />
  );

/* ─── by classes ─── */

/** Whether an account fits a class; null — an account of the other type, not counted. */
const useVerdictOf = () => {
  const { t } = useT();
  return (cls: ReadinessClass, id: string, caps: Map<string, CapabilityDef>): Verdict | null => {
    const cell = cls.cells[id];
    if (!cell || cell.status === 'type') return null;
    if (cell.status === 'ok')
      return { kind: 'ok', text: t('automation.project.readiness.verdict.ok') };
    if (cell.status === 'spent') {
      return {
        kind: 'missing',
        text: t('automation.project.readiness.verdict.spent'),
        spent: true,
      };
    }
    if (cell.status === 'missing') {
      return {
        kind: 'missing',
        text: t('automation.project.readiness.verdict.missing', {
          list: (cell.missing ?? []).map((k) => shortOf(caps, k)).join(', '),
        }),
        keys: cell.missing ?? [],
      };
    }
    if (!cell.unknown?.length) {
      return { kind: 'unknown', text: t('automation.project.readiness.verdict.unchecked') };
    }
    return {
      kind: 'unknown',
      text: t('automation.project.readiness.verdict.unknown', {
        list: cell.unknown.map((k) => shortOf(caps, k)).join(', '),
      }),
    };
  };
};

/**
 * Classes of the same type and requires get the same answer — one row per set
 * of requirements, the classes listed under it; the cells come from the first
 * class, as they depend on the type and requires only. One-off classes are not
 * merged: each has its own «used up» marks.
 */
interface Group {
  key: string;
  type: ReadinessClass['type'];
  requires: string[];
  unknownRequires: string[];
  fresh: boolean;
  classes: ReadinessClass[];
}

const groupClasses = (classes: ReadinessClass[]): Group[] => {
  const groups = new Map<string, Group>();
  for (const cls of classes) {
    const key = [
      cls.type ?? '*',
      [...cls.requires].sort().join(','),
      [...cls.unknownRequires].sort().join(','),
      cls.freshAccount ? cls.class : '',
    ].join('|');
    const group = groups.get(key) ?? {
      key,
      type: cls.type,
      requires: cls.requires,
      unknownRequires: cls.unknownRequires,
      fresh: cls.freshAccount,
      classes: [],
    };
    group.classes.push(cls);
    groups.set(key, group);
  }
  return [...groups.values()];
};

const FitBar = ({
  fit,
  total,
  type,
}: {
  fit: number;
  total: number;
  type: ReadinessClass['type'];
}) => {
  const { t } = useT();
  const pct = total ? Math.round((fit / total) * 100) : 0;
  return (
    <div className="max-w-44">
      <p className={cn('text-[13px]', fit ? undefined : 'text-fail')}>
        <b className="tabular-nums">{fit}</b>{' '}
        <span className="text-ink-subtle">
          {type
            ? t('automation.project.readiness.fitOfType', { total, type })
            : t('automation.project.readiness.fitOf', { total })}
        </span>
      </p>
      <div className="mt-1.5 h-1 rounded-sm bg-sunken" aria-hidden="true">
        <div
          className={cn('h-1 rounded-sm', fit ? 'bg-pass' : 'bg-fail')}
          style={{ width: `${Math.max(pct, fit ? 4 : 0)}%` }}
        />
      </div>
    </div>
  );
};

/** An expanded row: the accounts of its type — fitting first, then by reason. */
const AccountList = ({
  rows,
  className,
}: {
  rows: Array<{ account: ReadinessAccount; verdict: Verdict }>;
  className: string;
}) => {
  const order = { ok: 0, missing: 1, unknown: 2 };
  const nameOf = (a: ReadinessAccount) => a.companyName ?? a.label;
  const sorted = [...rows].sort(
    (x, y) =>
      order[x.verdict.kind] - order[y.verdict.kind] ||
      x.verdict.text.localeCompare(y.verdict.text) ||
      nameOf(x.account).localeCompare(nameOf(y.account)),
  );
  return (
    <ul className="divide-y divide-border py-1 pl-9 pr-3">
      {sorted.map(({ account, verdict }) => (
        <li
          key={account.id}
          className="grid grid-cols-1 gap-x-6 gap-y-1 py-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)]"
        >
          <AccountName account={account} />
          <div className="flex flex-wrap items-start gap-2 text-[12px]">
            <VerdictIcon kind={verdict.kind} />
            <span className={verdict.kind === 'ok' ? 'text-ink' : 'text-ink-muted'}>
              {verdict.text}
            </span>
            <FixButtons account={account} verdict={verdict} className={className} />
          </div>
        </li>
      ))}
    </ul>
  );
};

const ByClasses = ({
  classes,
  byId,
  caps,
}: {
  classes: ReadinessClass[];
  byId: Map<string, ReadinessAccount>;
  caps: Map<string, CapabilityDef>;
}) => {
  const { t } = useT();
  const verdictOf = useVerdictOf();
  const [open, setOpen] = useState<string | null>(null);
  const groups = useMemo(() => groupClasses(classes), [classes]);

  return (
    <Card>
      <TableWrap>
        <Table className="table-fixed">
          <colgroup>
            <col className="w-[44%]" />
            <col className="w-[22%]" />
            <col className="w-[34%]" />
          </colgroup>
          <thead>
            <tr>
              <Th className="normal-case tracking-normal">
                {t('automation.project.readiness.colNeeds')}
              </Th>
              <Th className="normal-case tracking-normal">
                {t('automation.project.readiness.colFit')}
              </Th>
              <Th className="normal-case tracking-normal">
                {t('automation.project.readiness.colNoFit')}
              </Th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const head = group.classes[0];
              if (!head) return null;
              const verdicts = Object.keys(head.cells).flatMap((id) => {
                const verdict = verdictOf(head, id, caps);
                const account = byId.get(id);
                return verdict && account ? [{ account, verdict }] : [];
              });
              const fit = verdicts.filter((v) => v.verdict.kind === 'ok').length;
              const reasons = new Map<string, { kind: Verdict['kind']; n: number }>();
              for (const { verdict } of verdicts) {
                if (verdict.kind !== 'ok') {
                  reasons.set(verdict.text, {
                    kind: verdict.kind,
                    n: (reasons.get(verdict.text)?.n ?? 0) + 1,
                  });
                }
              }
              // first «lacks …» — a gap in the pool, then «not checked» — a re-check cures it
              const sortedReasons = [...reasons.entries()].sort(
                (a, b) =>
                  Number(a[1].kind !== 'missing') - Number(b[1].kind !== 'missing') ||
                  b[1].n - a[1].n,
              );
              const isOpen = open === group.key;
              const toggle = () => setOpen(isOpen ? null : group.key);
              return (
                <Fragment key={group.key}>
                  <tr className="cursor-pointer align-top hover:bg-canvas" onClick={toggle}>
                    <Td className="align-top">
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggle();
                        }}
                        className="flex w-full items-start gap-1 text-left text-[13px]"
                      >
                        {isOpen ? (
                          <ChevronDown className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                        ) : (
                          <ChevronRight className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                        )}
                        <span className="min-w-0">
                          <span className="block">
                            {group.type ? (
                              <span className="text-ink-subtle">{group.type} · </span>
                            ) : null}
                            {group.requires.length ? (
                              group.requires.map((k) => shortOf(caps, k)).join(' · ')
                            ) : (
                              <span className="text-ink-subtle">
                                {t('automation.project.readiness.noRequirements')}
                              </span>
                            )}
                          </span>
                          {group.fresh ? (
                            <span
                              className="block text-[12px] text-block"
                              title={t('automation.project.readiness.freshHint')}
                            >
                              {t('automation.project.readiness.fresh')}
                            </span>
                          ) : null}
                          {group.unknownRequires.length > 0 ? (
                            <span className="block text-[12px] text-block">
                              {t('automation.project.readiness.unknownKeys', {
                                keys: group.unknownRequires.join(', '),
                              })}
                            </span>
                          ) : null}
                          <span className="mt-0.5 block break-words text-[12px] text-ink-subtle">
                            {group.classes.map((c) => c.class).join(', ')}
                          </span>
                        </span>
                      </button>
                    </Td>
                    <Td className="align-top">
                      <FitBar fit={fit} total={verdicts.length} type={group.type} />
                    </Td>
                    <Td className="align-top text-[12px]">
                      {sortedReasons.length ? (
                        <ul className="flex flex-col gap-1">
                          {sortedReasons.map(([text, reason]) => (
                            <li key={text} className="flex items-start gap-2">
                              <VerdictIcon kind={reason.kind} />
                              <span className="min-w-5 tabular-nums">{reason.n}</span>
                              <span className="text-ink-muted">{text}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-ink-subtle">{t('common.empty')}</span>
                      )}
                    </Td>
                  </tr>
                  {isOpen ? (
                    <tr>
                      <td colSpan={3} className="border-b border-border bg-canvas p-0">
                        <AccountList rows={verdicts} className={head.class} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>
    </Card>
  );
};

/* ─── by accounts ─── */

const CapIcon = ({
  result,
  account,
  capability,
}: {
  result: CapabilityResult | undefined;
  account: ReadinessAccount;
  capability: string;
}) => {
  const { t } = useT();
  const { repairs, canAct, repair } = useContext(ActionsContext);
  if (!result) return <span className="text-ink-subtle">{t('common.empty')}</span>;
  if (result.status === 'ok') {
    return (
      <span title={result.detail}>
        <CheckCircle2 className="inline size-4 text-pass" aria-label={result.detail} />
      </span>
    );
  }
  const repairTitle = repairs[capability];
  if (result.status === 'fail' && repairTitle && canAct && account.repair?.status !== 'running') {
    const label = t('automation.project.readiness.repairWith', {
      detail: result.detail,
      repair: repairTitle,
    });
    return (
      <button
        type="button"
        title={label}
        aria-label={label}
        onClick={() => repair(account, capability)}
        className="inline-flex items-center gap-0.5 rounded p-0.5 hover:bg-sunken"
      >
        <XCircle className="size-4 text-fail" aria-hidden="true" />
        <Wrench className="size-3 text-ink-subtle" aria-hidden="true" />
      </button>
    );
  }
  if (result.status === 'fail') {
    return (
      <span title={result.detail}>
        <XCircle className="inline size-4 text-fail" aria-label={result.detail} />
      </span>
    );
  }
  return (
    <span title={result.detail}>
      <CircleDashed className="inline size-4 text-ink-subtle" aria-label={result.detail} />
    </span>
  );
};

const ByAccounts = ({
  accounts,
  classes,
  caps,
}: {
  accounts: ReadinessAccount[];
  classes: ReadinessClass[];
  caps: Map<string, CapabilityDef>;
}) => {
  const { t } = useT();
  const { relative } = useFormat();
  // only what some class of the project requires: for cards that is 6 columns, not 14
  const required = [...caps.keys()].filter((k) => classes.some((c) => c.requires.includes(k)));
  const columns = required.length ? required : [...caps.keys()];
  const nameOf = (a: ReadinessAccount) => a.companyName ?? a.label;
  // checked ones first: an unchecked account has an empty row
  const rows = [...accounts].sort(
    (a, b) =>
      Number(!a.capabilities) - Number(!b.capabilities) ||
      a.type.localeCompare(b.type) ||
      nameOf(a).localeCompare(nameOf(b)),
  );

  return (
    <Card>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th className="w-12 normal-case tracking-normal">
                {t('automation.project.readiness.colType')}
              </Th>
              <Th className="min-w-64 normal-case tracking-normal">
                {t('automation.project.readiness.colAccount')}
              </Th>
              {columns.map((key) => (
                <Th
                  key={key}
                  title={caps.get(key)?.title}
                  className="w-24 whitespace-normal text-center normal-case tracking-normal"
                >
                  {shortOf(caps, key)}
                </Th>
              ))}
              <Th className="normal-case tracking-normal">
                {t('automation.project.readiness.colChecked')}
              </Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((account) => (
              <tr key={account.id}>
                <Td className="text-[12px] text-ink-subtle">{account.type}</Td>
                <Td className="max-w-sm">
                  <AccountName account={account} />
                </Td>
                {columns.map((key) => (
                  <Td key={key} className="text-center">
                    <CapIcon
                      result={account.capabilities?.[key]}
                      account={account}
                      capability={key}
                    />
                  </Td>
                ))}
                <Td className="whitespace-nowrap text-[12px] text-ink-subtle">
                  {account.capabilities
                    ? relative(account.checkedAt)
                    : t('automation.project.readiness.notChecked')}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
    </Card>
  );
};

/* ─── the matrix ─── */

type Pending =
  | { kind: 'repair'; account: ReadinessAccount; key: string }
  | { kind: 'unspend'; account: ReadinessAccount; className: string };

const Notice = ({ children, tone }: { children: ReactNode; tone: 'info' | 'warn' | 'muted' }) => (
  <div
    role="note"
    className={cn(
      'flex items-start gap-2 rounded-lg border px-3 py-2.5 text-[13px] text-ink',
      tone === 'info' && 'border-accent/30 bg-accent-soft',
      tone === 'warn' && 'border-block/40 bg-block-soft',
      tone === 'muted' && 'border-border bg-surface text-ink-muted',
    )}
  >
    {tone === 'info' ? (
      <Info className="mt-0.5 size-4 shrink-0 text-accent-fg" aria-hidden="true" />
    ) : null}
    {tone === 'warn' ? (
      <HelpCircle className="mt-0.5 size-4 shrink-0 text-block" aria-hidden="true" />
    ) : null}
    <div>{children}</div>
  </div>
);

/**
 * The readiness table of one project: its classes' requirements × the pool's
 * accounts, with repair and «back in use» for QA. The plan is read from TMS and
 * matched against the pool by TestRun; it refreshes while a repair runs.
 */
export const ReadinessMatrix = ({ projectId }: { projectId: string }) => {
  const { t } = useT();
  const errorText = useAutomationErrorText();
  const failToast = useFailToast();
  const can = useAutomationCan();
  const plan = useTestPlan(projectId);
  const readiness = useReadiness(projectId);
  const repairAccount = useRepairAccount();
  const unspendAccount = useUnspendAccount();
  const [view, setView] = useState<'classes' | 'accounts'>('classes');
  const [onlyWithRequires, setOnlyWithRequires] = useState(true);
  const [pending, setPending] = useState<Pending | null>(null);

  const data = readiness.data;
  const caps = useMemo(() => new Map((data?.capabilities ?? []).map((c) => [c.key, c])), [data]);
  const accounts = useMemo(() => data?.accounts ?? [], [data]);
  const byId = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  const classes = (data?.classes ?? []).filter(
    (c) => !onlyWithRequires || c.requires.length || c.unknownRequires.length || c.freshAccount,
  );
  const stale = accounts.filter((a) => !a.capabilities).length;

  const actions: Actions = {
    repairs: data?.repairs ?? {},
    canAct: can.run,
    repair: (account, key) => setPending({ kind: 'repair', account, key }),
    unspend: (account, className) => setPending({ kind: 'unspend', account, className }),
  };

  const confirm = () => {
    if (!pending) return;
    if (pending.kind === 'repair') {
      const repair = data?.repairs[pending.key] ?? pending.key;
      repairAccount.mutate(
        { id: pending.account.id, key: pending.key },
        {
          onSuccess: () => {
            toast.success(t('automation.project.readiness.repairStarted', { repair }));
            setPending(null);
          },
          onError: (error) => {
            setPending(null);
            failToast(error);
          },
        },
      );
    } else {
      const { account, className } = pending;
      unspendAccount.mutate(
        { id: account.id, className },
        {
          onSuccess: () => {
            toast.success(
              t('automation.project.readiness.unspent', {
                account: accountTitle(account),
                className,
              }),
            );
            setPending(null);
          },
          onError: (error) => {
            setPending(null);
            failToast(error);
          },
        },
      );
    }
  };

  const loadError = plan.error ?? readiness.error;
  const body = (() => {
    if (loadError) {
      return (
        <Card>
          <ErrorState
            error={new Error(errorText(loadError))}
            onRetry={() => void (plan.error ? plan.refetch() : readiness.refetch())}
          />
        </Card>
      );
    }
    if (plan.isSuccess && plan.data === null) {
      return <Notice tone="muted">{t('automation.project.readiness.notScanned')}</Notice>;
    }
    if (!data) return <SkeletonRows rows={4} className="p-0" />;
    if (!accounts.length)
      return <Notice tone="muted">{t('automation.project.readiness.emptyPool')}</Notice>;
    if (view === 'accounts')
      return <ByAccounts accounts={accounts} classes={data.classes} caps={caps} />;
    if (!classes.length) {
      return (
        <Notice tone="muted">
          {data.classes.length
            ? t('automation.project.readiness.noRequires')
            : t('automation.project.readiness.noClasses')}
        </Notice>
      );
    }
    return <ByClasses classes={classes} byId={byId} caps={caps} />;
  })();

  const warnKey = pending?.kind === 'repair' ? REPAIR_WARN[pending.key] : undefined;

  return (
    <ActionsContext.Provider value={actions}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Segmented
              label={t('automation.project.readiness.view')}
              value={view}
              onChange={setView}
              options={[
                { value: 'classes', label: t('automation.project.readiness.byClasses') },
                { value: 'accounts', label: t('automation.project.readiness.byAccounts') },
              ]}
            />
            {view === 'classes' ? (
              <label className="flex items-center gap-2 text-[13px] text-ink-muted">
                <input
                  type="checkbox"
                  checked={onlyWithRequires}
                  onChange={(event) => setOnlyWithRequires(event.target.checked)}
                  className="size-4 rounded border-border"
                />
                {t('automation.project.readiness.onlyWithRequires')}
              </label>
            ) : null}
          </div>
          <p className="text-[12px] text-ink-subtle">{t('automation.project.readiness.legend')}</p>
        </div>

        <Notice tone="info">{t('automation.project.readiness.info')}</Notice>

        {stale > 0 ? (
          <Notice tone="warn">
            {t('automation.project.readiness.stale', { stale, total: accounts.length })}{' '}
            <Link to="/automation/accounts" className="font-medium text-accent-fg hover:underline">
              {t('automation.project.readiness.openAccounts')}
            </Link>
          </Notice>
        ) : null}

        {body}
      </div>

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => (open ? undefined : setPending(null))}
        title={
          pending?.kind === 'repair'
            ? t('automation.project.readiness.repairTitle', {
                repair: data?.repairs[pending.key] ?? pending.key,
                account: accountTitle(pending.account),
              })
            : pending
              ? t('automation.project.readiness.unspendTitle', {
                  account: accountTitle(pending.account),
                  className: pending.className,
                })
              : ''
        }
        description={
          pending?.kind === 'repair'
            ? [warnKey ? t(warnKey) : '', t('automation.project.readiness.repairAfter')]
                .filter(Boolean)
                .join(' ')
            : t('automation.project.readiness.unspendDescription')
        }
        confirmLabel={
          pending?.kind === 'repair'
            ? t('automation.project.readiness.repairConfirm')
            : t('automation.project.readiness.unspend')
        }
        destructive={false}
        loading={repairAccount.isPending || unspendAccount.isPending}
        onConfirm={confirm}
      />
    </ActionsContext.Provider>
  );
};
